import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  Paper,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";

import MenuBar from "./MenuBar";
import Footer from "./Footer";
import envVariable from "../EnvironmentVariables.json";
import { SPECIALITY_OPTIONS, normalizeSpecialities } from "../constants/specialities";
import "./Style.css";

const Account = () => {
  const [accountDetails, setAccountDetails] = useState({
    scName: "",
    fName: "",
    lName: "",
    email: "",
    personalEmail: "",
    scoContactNumber: "",
    personalNumber: "",
    addressLine: "",
    addressLine2: "",
    zipcode: "",
    city: "",
    state: "",
  });

  // NEW: locations pulled from DB .
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [featureModalOpen, setFeatureModalOpen] = useState(false);
  const [featureDraft, setFeatureDraft] = useState([]);

  //New modal UI
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const MAX_LOCATIONS = 5;

  const [scImage, setScImage] = useState(undefined);
  const [errors, setErrors] = useState({});
  const [editorOpened, setEditorOpened] = useState(false);
  const [hasMechanicProfile, setHasMechanicProfile] = useState(false);
  const [isCreatingMechanicProfile, setIsCreatingMechanicProfile] = useState(false);
  
  const navigate = useNavigate();

  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const validStates = ["Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", 
                      "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", 
                      "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", 
                      "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", 
                      "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York", 
                      "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", 
                      "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", 
                      "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming",
                      "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", 
                      "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV",
                      "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", 
                      "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"];
  

  //const hasOtherLocations = useMemo(() => (locations?.length || 0) > 1, [locations]);

  // Helper: apply a location object onto the existing form fields
  const applyLocationToForm = (loc) => {
    if (!loc) return;
    setAccountDetails((prev) => ({
      ...prev,
      scoContactNumber: loc.phone ?? "",
      addressLine: loc.address1 ?? "",
      addressLine2: loc.address2 ?? "",
      zipcode: loc.zipcode ?? "",
      city: loc.city ?? "",
      state: loc.state ?? "",
    }));
  };

  //MODAL FUNCTIONALITY
  const makeEmptySecondaryLocation = () => ({
    location_id: -Date.now(), // temporary frontend-only ID (negative so it won't clash)
    nickname: "",
    phone: "",
    address1: "",
    address2: "",
    zipcode: "",
    city: "",
    state: "",
    country: "USA",
    latitude: "0.0",
    longitude: "0.0",
    is_primary: 0,
    specialities: [],
  });
  const getLocationLabel = (loc, idx = 0) => {
    if (!loc) return "Select Location";
    const base = loc.nickname?.trim() ? loc.nickname.trim() : `Location ${idx + 1}`;
    return Number(loc.is_primary) === 1 ? `${base} (Primary)` : base;
  };
  
  const handleOpenLocationModal = () => setLocationModalOpen(true);
  const handleCloseLocationModal = () => setLocationModalOpen(false);
  const handleOpenFeatureModal = () => {
    const selectedLocation = locations.find(
      (location) => Number(location.location_id) === Number(selectedLocationId)
    );
    setFeatureDraft(normalizeSpecialities(selectedLocation?.specialities || []));
    setFeatureModalOpen(true);
  };
  const handleCloseFeatureModal = () => {
    setFeatureDraft([]);
    setFeatureModalOpen(false);
  };
  
  const handleAddLocationFromModal = () => {
    if (locations.length >= MAX_LOCATIONS) {
      alert("Maximum of 5 locations allowed.");
      return;
    }
  
    const newLoc = makeEmptySecondaryLocation();
  
    setLocations((prev) => [...prev, newLoc]);
    setSelectedLocationId(newLoc.location_id);
    applyLocationToForm(newLoc);
    setLocationModalOpen(false);
    setEditorOpened(true);
  };
  //END MODAL FUNC


  // eslint-disable-next-line react-hooks/set-state-in-effect
  const fetchProfileInfo = useCallback(async () => {
    const sc_id = localStorage.getItem("SC_Id");
    try {
      const response = await fetch(
        `${envVariable.serverId}/SCO/getProfileInfo.php?sc_id=${sc_id}`
      );
      const responseJson = await response.json();
      console.log("API response:", responseJson);
      console.log("primary row:", responseJson.message?.[0]);

      if (responseJson.type !== "success") {
        console.error("Error message from API:", responseJson.message);
        return;
      }

      // Backwards compatible primary record is message[0]
      const primary = responseJson.message?.[0];
      const locs = responseJson.message?.locations || [];
      setHasMechanicProfile(Boolean(primary?.has_mechanic_profile));

      setLocations(
        locs.map((location) => ({
          ...location,
          specialities: normalizeSpecialities(location.specialities || []),
        }))
      );

      // Select primary location by default
      const primaryLocId =
        locs.find((l) => Number(l.is_primary) === 1)?.location_id ??
        locs[0]?.location_id ??
        null;

      setSelectedLocationId(primaryLocId);

      // Populate non-location fields from profile
      setAccountDetails({
        scName: primary?.sc_name || "",
        fName: primary?.first_name || "",
        lName: primary?.last_name || "",
        email: primary?.sco_email || "",
        personalEmail: primary?.personal_email || "",
        personalNumber: primary?.personal_phone || "",

        // Populate location fields from PRIMARY
        scoContactNumber: primary?.phone || "",
        addressLine: primary?.address1 || "",
        addressLine2: primary?.address2 || "",
        zipcode: primary?.zipcode || "",
        city: primary?.city || "",
        state: primary?.state || "",
      });
    } catch (error) {
      console.error("Error fetching profile info:", error);
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("isAuthenticated")) {
      navigate("/login");
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchProfileInfo();
  }, [navigate, fetchProfileInfo]);

    /*let isMounted = true;

    const loadProfile = async () => {
      if (isMounted) {
        await fetchProfileInfo();
      }
    };
    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [navigate, fetchProfileInfo]);*/

  // NEW: when user selects a location from the menu, populate form fields from DB list
  const handleLocationSelect = (locationId) => {
    setSelectedLocationId(locationId);
    const loc = locations.find((l) => Number(l.location_id) === Number(locationId));
    applyLocationToForm(loc);
  };

  const handleFileChange = (e) => setScImage(e.target.files[0]);

  const enableMechanicProfile = async () => {
    if (hasMechanicProfile || isCreatingMechanicProfile) return;

    const sc_id = localStorage.getItem("SC_Id");
    const user_id = localStorage.getItem("User_Id");
    setIsCreatingMechanicProfile(true);

    try {
      const response = await fetch(`${envVariable.serverId}/SCO/enableMechanicProfile.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sc_id, user_id }),
      });

      const responseJson = await response.json();
      if (responseJson.type === "success") {
        setHasMechanicProfile(true);
      } else {
        alert(responseJson.message || "Unable to create mechanic profile");
      }
    } catch (error) {
      console.error("Error enabling mechanic profile:", error);
      alert("Unable to create mechanic profile right now.");
    } finally {
      setIsCreatingMechanicProfile(false);
    }
  };

  const validateFields = (fields) => {
    const newErrors = {};
    if (!fields.scName) newErrors.scName = "Please input a name";
    if (!fields.fName) newErrors.fName = "Please input a first name";
    if (!fields.lName) newErrors.lName = "Please input a last name";
    if (!fields.email || !fields.email.includes("@"))
      newErrors.email = "Please input a valid email address";
    if (!fields.personalEmail || !fields.personalEmail.includes("@"))
      newErrors.personalEmail = "Please input a valid email address";

    if (!fields.scoContactNumber || String(fields.scoContactNumber).replace(/\D/g, "").length !== 10)
      newErrors.scoContactNumber = "Please input a 10-digit phone number";
    if (!fields.personalNumber || String(fields.personalNumber).replace(/\D/g, "").length !== 10)
      newErrors.personalNumber = "Please input a 10-digit phone number";

    if (!fields.addressLine) newErrors.addressLine = "Please input an address";
    if (!fields.zipcode || fields.zipcode.length !== 5)
      newErrors.zipcode = "Please input a valid 5-digit zipcode";
    if (!fields.city) newErrors.city = "Please input a city";
    if (!fields.state || !validStates.includes(fields.state)) newErrors.state = "Please input a valid state";
    setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
  };

  const toggleFeatureDraft = (specialityId, checked) => {
    setFeatureDraft((prev) =>
      checked
        ? normalizeSpecialities([...prev, specialityId])
        : prev.filter((item) => item !== specialityId)
    );
  };

  const saveFeatureDraft = () => {
    setLocations((prev) =>
      prev.map((location) =>
        Number(location.location_id) === Number(selectedLocationId)
          ? { ...location, specialities: normalizeSpecialities(featureDraft) }
          : location
      )
    );
    handleCloseFeatureModal();
  };

    // SUBMIT: update ONLY selected location (and profile/user fields)
  const submitChanges = async (e) => {
    e.preventDefault();
    const updatedFields = { ...accountDetails };
    if (!validateFields(updatedFields)) return;

    const sc_id = localStorage.getItem("SC_Id");
    const user_id = localStorage.getItem("User_Id");

    // Build the "current location" payload only
    const loc = locations.find((l) => Number(l.location_id) === Number(selectedLocationId));

    //said function implemented above
    const rawLocId = Number(loc?.location_id);
    const isTempLocation = Number.isFinite(rawLocId) && rawLocId < 0;


    const locationPayload = {
      location_id: isTempLocation ? null : (loc?.location_id ?? null),  //use function implemented above
      phone: String(updatedFields.scoContactNumber || "").replace(/\D/g, ""),
      address1: updatedFields.addressLine,
      address2: updatedFields.addressLine2 || "",
      zipcode: String(updatedFields.zipcode || ""),
      city: updatedFields.city,
      state: updatedFields.state,
      // keep nickname/country/lat/lng from DB row unless you add fields to edit them
      nickname: loc?.nickname ?? "",
      country: loc?.country ?? "USA",
      latitude: loc?.latitude ?? "0.0",
      longitude: loc?.longitude ?? "0.0",
      is_primary: Number(loc?.is_primary ?? 0),
      specialities: normalizeSpecialities(loc?.specialities || []),
    };

    const payload = {
      sc_id,
      user_id,
      profile: {
        scName: updatedFields.scName,
        fName: updatedFields.fName,
        lName: updatedFields.lName,
        email: updatedFields.email,
        personalEmail: updatedFields.personalEmail,
        scoContactNumber: String(updatedFields.personalNumber || "").replace(/\D/g, ""),
      },
      // ONLY current location
      location: locationPayload,
    };

    try {
      const response = await fetch(`${envVariable.serverId}/SCO/editProfileInfo.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();
      let responseJson;
      try {
        responseJson = JSON.parse(responseText);
      } catch {
        console.log("Raw response:", responseText);
        alert("Backend did not return JSON. Check console.");
        return;
      }

      if (responseJson.type === "success") {
        setEditorOpened(false);
        await fetchProfileInfo(); // refresh locations + form
      } else {
        alert(responseJson.message || "Update failed");
      }

      // image upload unchanged
      if (scImage) {
        const formData = new FormData();
        formData.append("sc_id", sc_id);
        formData.append("imageDat", scImage);
        await fetch(`${envVariable.serverId}/SCO/editScImage.php`, {
          method: "POST",
          body: formData,
        });
      }

      //Display "Success" on good submit
      if (responseJson.type === "success") {
        setEditorOpened(false);
        setSuccessSnackbarOpen(true);  // ← add this line
        await fetchProfileInfo();
      }

    } catch (error) {
      console.error("Error submitting changes:", error);
    }
  };

  return (
    <>
      <MenuBar />
      <Box p={3}>
        <Typography variant="h4" gutterBottom>
          Profile Information
        </Typography>
        <Box component="form" onSubmit={submitChanges}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Service Center Name"
                name="scName"
                required
                margin="normal"
                value={accountDetails.scName || ""}
                onChange={(e) =>
                  setAccountDetails({
                    ...accountDetails,
                    scName: e.target.value,
                  })
                }
                error={!!errors.scName}
                helperText={errors.scName}
                disabled={!editorOpened}
              />
            </Grid>
            <Grid item xs={12} sm={6} display="flex" alignItems="center">
              <Typography variant="body1" mr={2}>
                Service Center Image
              </Typography>
              <Button
                variant="contained"
                component="label"
                color="primary"
                disabled={!editorOpened}
              >
                Choose File
                <input type="file" hidden onChange={handleFileChange} />
              </Button>
            </Grid>

            {[
              { label: "First Name", name: "fName", required: true },
              { label: "Last Name", name: "lName", required: true },
              { label: "SC Email", name: "email", required: true, type: "email" },
              {
                label: "Personal Email",
                name: "personalEmail",
                required: true,
                type: "email",
              },
              {
                label: "SC Contact Number",
                name: "scoContactNumber",
                required: true,
                type: "tel",
              },
              {
                label: "Personal Contact Number",
                name: "personalNumber",
                required: true,
                type: "tel",
              },
              { label: "Address Line 1", name: "addressLine", required: true },
              { label: "Address Line 2", name: "addressLine2" },
              { label: "City", name: "city", required: true },
              { label: "State", name: "state", required: true },
              {
                label: "Zip Code",
                name: "zipcode",
                required: true,
                type: "number",
              },
            ].map(({ label, name, required, type }) => (
              <Grid item xs={12} sm={6} key={name}>
                <TextField
                  fullWidth
                  label={label}
                  name={name}
                  type={type || "text"}
                  required={required}
                  margin="normal"
                  value={accountDetails[name] || ""}
                  onChange={(e) =>
                    setAccountDetails({
                      ...accountDetails,
                      [name]: e.target.value,
                    })
                  }
                  error={!!errors[name]}
                  helperText={errors[name]}
                  disabled={!editorOpened}
                />
              </Grid>
            ))}

          </Grid>
          {scImage && (
            <Typography mt={1}>Selected file: {scImage.name}</Typography>
          )}

          <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
              <Button variant="contained" onClick={handleOpenLocationModal}>
                Manage Locations
              </Button>
              <Button
                variant="contained"
                onClick={handleOpenFeatureModal}
                disabled={!editorOpened || !selectedLocationId}
              >
                Manage Features
              </Button>

              <Typography color="text.secondary">
                Current location:{" "}
                {(() => {
                  const idx = locations.findIndex(
                    (l) => Number(l.location_id) === Number(selectedLocationId)
                  );
                  const loc = idx >= 0 ? locations[idx] : null;
                  return getLocationLabel(loc, idx >= 0 ? idx : 0);
                })()}
              </Typography>
            </Box>

            <Dialog
              open={locationModalOpen}
              onClose={handleCloseLocationModal}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>Select Location</DialogTitle>

              <DialogContent dividers>
                {locations.length === 0 ? (
                  <Typography color="text.secondary">
                    No locations found.
                  </Typography>
                ) : (
                  <List disablePadding>
                    {locations.map((l, idx) => {
                      const isSelected =
                        Number(l.location_id) === Number(selectedLocationId);

                      return (
                        <React.Fragment key={l.location_id ?? idx}>
                          <ListItemButton
                            selected={isSelected}
                            onClick={() => {
                              handleLocationSelect(l.location_id);
                              handleCloseLocationModal();
                            }}
                          >
                            <ListItemText
                              primary={getLocationLabel(l, idx)}
                              secondary={
                                [l.address1, l.city, l.state, l.zipcode]
                                  .filter(Boolean)
                                  .join(", ") || "No address entered yet"
                              }
                            />
                          </ListItemButton>
                          {idx < locations.length - 1 && <Divider />}
                        </React.Fragment>
                      );
                    })}
                  </List>
                )}
              </DialogContent>

              <DialogActions sx={{ p: 2 }}>
              <Box sx={{ flexGrow: 1, pl: 1 }}>
                <Typography variant="body2" color={locations.length >= MAX_LOCATIONS ? "error" : "text.secondary"}>
                  {locations.length}/{MAX_LOCATIONS} locations used
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={handleAddLocationFromModal}
                disabled={!editorOpened || locations.length >= MAX_LOCATIONS}
              >
                Add Location
              </Button>

                <Button onClick={handleCloseLocationModal}>Close</Button>
              </DialogActions>
            </Dialog>
          </Paper>

          <Dialog
            open={featureModalOpen}
            onClose={handleCloseFeatureModal}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle>Location Features</DialogTitle>
            <DialogContent dividers>
              <FormControl component="fieldset" sx={{ width: "100%" }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Select features for the current location
                </Typography>
                <FormGroup row>
                  {SPECIALITY_OPTIONS.map((option) => (
                    <FormControlLabel
                      key={option.id}
                      control={
                        <Checkbox
                          checked={featureDraft.includes(option.id)}
                          onChange={(e) => toggleFeatureDraft(option.id, e.target.checked)}
                          disabled={!editorOpened}
                        />
                      }
                      label={option.label}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={handleCloseFeatureModal}>Cancel</Button>
              <Button
                variant="contained"
                onClick={saveFeatureDraft}
                disabled={!editorOpened}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
          <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Mechanic Profile
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Enable a mechanic profile for this SCO account so the same credentials can be used in the mechanic mobile flow.
            </Typography>
            <Button
              variant="contained"
              onClick={enableMechanicProfile}
              disabled={hasMechanicProfile || isCreatingMechanicProfile}
            >
              {hasMechanicProfile
                ? "Mechanic Profile Enabled"
                : (isCreatingMechanicProfile ? "Enabling..." : "Enable Mechanic Profile")}
            </Button>
          </Paper>

          <Box mt={3}>
            {!editorOpened ? (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setEditorOpened(true)}
                sx={{ mr: 2 }}
              >
                Edit Business Info
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  sx={{ backgroundColor: "grey.500", color: "white", mr: 2 }}
                  onClick={() => setEditorOpened(false)}
                >
                  Cancel
                </Button>
                <Button variant="contained" color="primary" type="submit">
                  Submit
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSuccessSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessSnackbarOpen(false)}
          severity="success"
          variant="filled"
        >
          Location verified successfully!
        </Alert>
      </Snackbar>


      <Footer />
    </>
  );
};

export default Account;
